import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { API, api } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";

const DOC_STYLES = `
  .api-doc h1 { font-size: 1.6rem; margin: 0 0 1rem; }
  .api-doc h2 { font-size: 1.25rem; margin: 2.2rem 0 0.6rem; padding-top: 1.2rem; border-top: 1px solid hsl(var(--border)); }
  .api-doc h3 { font-size: 1.05rem; margin: 1.6rem 0 0.4rem; }
  .api-doc h4 { font-size: 0.95rem; margin: 1.2rem 0 0.3rem; }
  .api-doc p, .api-doc li { font-size: 0.9rem; line-height: 1.65; }
  .api-doc pre { background: hsl(var(--secondary)); border: 1px solid hsl(var(--border)); border-radius: 4px; padding: 0.9rem 1rem; overflow-x: auto; margin: 0.7rem 0; }
  .api-doc pre code { background: transparent; padding: 0; font-size: 0.78rem; line-height: 1.55; }
  .api-doc code { background: hsl(var(--secondary)); border-radius: 3px; padding: 0.1rem 0.35rem; font-size: 0.8rem; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  .api-doc table { border-collapse: collapse; width: 100%; margin: 0.8rem 0; font-size: 0.85rem; display: block; overflow-x: auto; }
  .api-doc th, .api-doc td { border: 1px solid hsl(var(--border)); padding: 0.45rem 0.7rem; text-align: left; vertical-align: top; }
  .api-doc th { background: hsl(var(--secondary)); font-weight: 600; white-space: nowrap; }
  .api-doc blockquote { border-left: 3px solid hsl(var(--primary)); padding-left: 0.9rem; color: hsl(var(--muted-foreground)); margin: 0.8rem 0; }
  .api-doc a { color: hsl(var(--primary)); text-decoration: underline; }
`;

export default function ApiDocs() {
  const [doc, setDoc] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/docs/api-reference", { responseType: "text" })
      .then((res) => setDoc(typeof res.data === "string" ? res.data : String(res.data)))
      .catch(() => toast.error("Could not load the API reference"))
      .finally(() => setLoading(false));
  }, []);

  const download = () => {
    const blob = new Blob([doc], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "API_DOCS.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl" data-testid="api-docs-page">
      <style>{DOC_STYLES}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/settings"
            data-testid="api-docs-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4" /> Settings
          </Link>
          <h1 className="mt-2 text-2xl font-heading font-semibold inline-flex items-center gap-2">
            <BookOpen className="h-6 w-6" /> API reference
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every endpoint, the two ways to authenticate, and copy-paste recipes for connecting other
            apps. Raw file: <code className="text-xs">GET {API}/docs/api-reference</code>
          </p>
        </div>
        <Button
          data-testid="api-docs-download-btn"
          variant="outline"
          onClick={download}
          disabled={!doc}
          className="rounded-full h-10 px-5"
        >
          <Download className="h-4 w-4 mr-2" /> Download .md
        </Button>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center" data-testid="api-docs-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="surface mt-6 p-6 lg:p-10" data-testid="api-docs-content-wrap">
          <Markdown text={doc} className="api-doc" testId="api-docs-content" />
        </div>
      )}
    </div>
  );
}
